/**
 * Boot-phase instrumentation.
 *
 * Answers "where did the wall-clock before first paint actually go?" by marking
 * the boot phases that can each independently block render, then correlating them
 * against the browser's own paint timings.
 *
 * The phases worth watching in this app, in order:
 *   html          - navigation start -> the entry module starts executing
 *   bundle-eval   - entry module executing (parse/compile/eval of the main chunk)
 *   react-mount   - createRoot().render() called
 *   persist-gate  - redux-persist rehydration (PersistGate renders null until done)
 *   remote-config - Firebase fetchAndActivate (FirebaseProvider returns null until done)
 *   first-paint   - browser FCP / LCP
 *
 * Enable in any environment with `?perf=1` or localStorage.setItem('iwc:perf','1').
 * Always enabled in dev.
 */

const NS = "iwc";

export const perfEnabled = (() => {
  if (typeof window === "undefined") return false;
  if (import.meta.env.DEV) return true;
  try {
    if (new URLSearchParams(window.location.search).has("perf")) return true;
    return window.localStorage.getItem(`${NS}:perf`) === "1";
  } catch {
    return false;
  }
})();

/** Mark the start of a boot phase. */
export function bootMarkStart(phase: string): void {
  if (!perfEnabled) return;
  performance.mark(`${NS}:${phase}:start`);
}

/** Mark the end of a boot phase and record its duration. */
export function bootMarkEnd(phase: string): void {
  if (!perfEnabled) return;
  const start = `${NS}:${phase}:start`;
  performance.mark(`${NS}:${phase}:end`);
  try {
    performance.measure(`${NS}:${phase}`, start, `${NS}:${phase}:end`);
  } catch {
    // start mark missing - measure from navigation start instead
    performance.measure(`${NS}:${phase}`, undefined, `${NS}:${phase}:end`);
  }
}

/** Wrap an async boot phase so it is measured whether it resolves or throws. */
export async function bootPhase<T>(
  phase: string,
  fn: () => Promise<T>
): Promise<T> {
  bootMarkStart(phase);
  try {
    return await fn();
  } finally {
    bootMarkEnd(phase);
  }
}

type PaintTimings = {
  ttfb?: number;
  domInteractive?: number;
  fcp?: number;
  lcp?: number;
};

const paints: PaintTimings = {};

function observePaints(): void {
  if (typeof PerformanceObserver === "undefined") return;

  const nav = performance.getEntriesByType(
    "navigation"
  )[0] as PerformanceNavigationTiming | undefined;
  if (nav) {
    paints.ttfb = nav.responseStart;
    paints.domInteractive = nav.domInteractive;
  }

  try {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (e.name === "first-contentful-paint") paints.fcp = e.startTime;
      }
    }).observe({ type: "paint", buffered: true });
  } catch { /* unsupported */ }

  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) paints.lcp = last.startTime;
    }).observe({ type: "largest-contentful-paint", buffered: true });
  } catch { /* unsupported */ }
}

const ms = (n: number | undefined) =>
  n === undefined ? "-" : `${n.toFixed(0)}ms`;

/** Collect every recorded phase + paint timing as plain data. */
export function bootReport() {
  const phases = performance
    .getEntriesByType("measure")
    .filter((m) => m.name.startsWith(`${NS}:`))
    .map((m) => ({
      phase: m.name.slice(NS.length + 1),
      startedAt: Math.round(m.startTime),
      duration: Math.round(m.duration),
    }))
    .sort((a, b) => a.startedAt - b.startedAt);

  const resources = performance
    .getEntriesByType("resource")
    .filter((r): r is PerformanceResourceTiming => "transferSize" in r);

  const scripts = resources
    .filter((r) => r.initiatorType === "script" || r.name.endsWith(".js"))
    .map((r) => ({
      url: r.name.split("/").pop() ?? r.name,
      origin: new URL(r.name, location.href).origin,
      transferKB: Math.round(r.transferSize / 1024),
      durationMs: Math.round(r.duration),
    }))
    .sort((a, b) => b.transferKB - a.transferKB);

  // Third-party origins are a common hidden cost - surface them separately.
  const byOrigin = new Map<string, { transferKB: number; count: number }>();
  for (const r of resources) {
    const origin = new URL(r.name, location.href).origin;
    const cur = byOrigin.get(origin) ?? { transferKB: 0, count: 0 };
    cur.transferKB += Math.round(r.transferSize / 1024);
    cur.count += 1;
    byOrigin.set(origin, cur);
  }

  return {
    paints,
    phases,
    scripts,
    origins: [...byOrigin]
      .map(([origin, v]) => ({ origin, ...v }))
      .sort((a, b) => b.transferKB - a.transferKB),
  };
}

/** Print the boot report. Called automatically once the page settles. */
export function logBootReport(): void {
  if (!perfEnabled) return;
  const { paints: p, phases, scripts, origins } = bootReport();

  console.group(
    `%c[boot-perf] FCP ${ms(p.fcp)} · LCP ${ms(p.lcp)} · TTFB ${ms(p.ttfb)}`,
    "color:#6a5147;font-weight:bold"
  );
  console.log(
    "Blocking phases (a phase that ends after FCP is delaying your paint):"
  );
  console.table(phases);
  console.log("Scripts by transfer size:");
  console.table(scripts.slice(0, 15));
  console.log("Bytes by origin (third parties included):");
  console.table(origins);
  console.groupEnd();
}

/** Install observers. Call as early as possible in the entry module. */
export function initBootPerf(): void {
  if (!perfEnabled) return;
  observePaints();

  // Report once the page has settled, so LCP has stabilised.
  const fire = () => setTimeout(logBootReport, 0);
  if (document.readyState === "complete") fire();
  else window.addEventListener("load", fire, { once: true });

  // Expose for manual inspection from the console.
  (window as unknown as Record<string, unknown>).__IWC_PERF__ = bootReport;
}
